import Vue from "vue";
import * as helper from "@/helpers";
import viewTypes from "@/views";
import billingService from "@/services/billing/billing.service";
import smartStorage from "@/helpers/smart-storage";

/**
 * Shared buy-email-accounts flow.
 *
 * Extracted from EmailAccounts.vue so nudge surfaces (success-screen nudge,
 * pending-accounts screen) can route through the same path with an account
 * count. Behaviour matches the original:
 *
 *  - Partner billing (`flags.disableIFramePaymentRedirection` inside an
 *    iframe) → `helper.handleRedirectionOnPartnerSide(jwt)`.
 *  - Titan billing → `inAppPayment` routes to `/buy-email-account` (paid
 *    plan) or `/billing-and-upgrade` (free plan) inside the CP; any other
 *    `makePaymentUrl` does a hard redirect, optionally in a new tab when
 *    the view config wants payment URLs in a separate tab.
 *
 * Components include this mixin and call `triggerBuyFlow({ count,
 *   sourceHook })`. `count` is informational (flows through to the buy
 *   page via the existing `redirectionSource` query param convention).
 */

export type TBuyFlowOptions = {
  count?: number;
  sourceHook?: string;
};

type TBuyFlowPaymentData = {
  jwt: string;
  makePaymentUrl: string;
};

export default Vue.extend({
  computed: {
    /**
     * Whether the current view config opens payment URLs in a new tab.
     * A computed (not data) for two reasons: it keeps the DOM read out of
     * `data()` (testable), and Vue 2 never proxies `_`-prefixed *data* keys
     * onto the instance — as data this flag silently read as undefined.
     */
    _buyFlowOpenPaymentURLInDiffTab(): boolean {
      const view = document.documentElement.getAttribute("view") || "";
      return !!viewTypes[view]?.["openPaymentURLInDiffTab"];
    },
  },
  methods: {
    /**
     * Kick off the buy/upgrade flow. Returns the underlying service promise
     * so callers can chain. `count` and `sourceHook` are passed through to
     * the existing redirect query convention.
     */
    triggerBuyFlow({
      count = 1,
      sourceHook = "mailbox_limit_popup",
    }: TBuyFlowOptions = {}): Promise<unknown> {
      const action = this._buyFlowIsPaid() ? "buyMoreAccounts" : "upgradePlan";
      return billingService
        .checkPaymentType(action)
        .then((data: TBuyFlowPaymentData) => this._buyFlowRedirect(data, { count, sourceHook }));
    },
    _buyFlowIsPaid(): boolean {
      // Same check the host components use today — currentProductDetails.isPaid.
      return !!this.$store.getters["mail/currentProductDetails"]?.isPaid;
    },
    _buyFlowRedirect(
      data: TBuyFlowPaymentData,
      { count, sourceHook }: Required<TBuyFlowOptions>
    ): Promise<unknown> | void {
      const eventObj = {
        eventName: "upgrade",
        source: helper.getAppSource(),
      };
      // Partner-handled billing in iframe → hand off to the partner side,
      // including how many accounts the user wants.
      if (
        helper.isAppLoadedInsideIframe() &&
        this.$store.getters.flags?.disableIFramePaymentRedirection
      ) {
        helper.handleRedirectionOnPartnerSide(data.jwt, count);
        return;
      }
      const cpURL = encodeURIComponent(`${window.location.origin}/postPayment`);
      const redirectURL = `${data.makePaymentUrl}?jwt=${data.jwt}&redirect_url=${cpURL}`;
      return this.medusaEvents
        .send(this.medusaEvents.createEventObj(eventObj, helper.getDomainName(), "flock_order"))
        .then(() => {
          if (data.makePaymentUrl === "inAppPayment") {
            this.$router.push({
              path: this._buyFlowIsPaid() ? "/buy-email-account" : "/billing-and-upgrade",
              query: {
                redirectionSource: sourceHook,
                count: String(count),
              },
            });
            return false;
          }
          smartStorage.hostRedirectURL = redirectURL;
          helper.hardRedirect(
            redirectURL,
            false,
            this._buyFlowOpenPaymentURLInDiffTab && data.makePaymentUrl !== "inAppPayment"
          );
        })
        .catch(() => {
          smartStorage.hostRedirectURL = redirectURL;
          helper.hardRedirect(redirectURL);
        });
    },
  },
});
